import React, { FC, useCallback, useRef, useState } from 'react';
import { FileUpload } from 'components/forms';
import { FieldRenderProps } from 'react-final-form';
import { convertFileToBase64, convertBytesToMB, findDuplicatesInArray } from 'utils';

const getHeightAndWidthFromDataUrl = (dataURL: any): any =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve({
        height: img.height,
        width: img.width,
      });
    };
    img.src = dataURL;
  });

// Get dimensions
const getImgDimensions = async (url: any): Promise<any> => {
  const dimensions = await getHeightAndWidthFromDataUrl(url);
  return dimensions;
};

// Allowed dimensions for images in gallery; could be updated to prop
const allowedImageDimensions = { width: 468, height: 216 };

const ScreenshotsUploadWrapper: FC<FieldRenderProps<[]>> = (props) => {
  const { render, children, input, maxFiles = 4, ...otherProps } = props;
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const handleChange = useCallback(
    async (files) => {
      setError('');
      const filesArray = Array.prototype.slice.call(files, 0, maxFiles);
      const filesTooLarge = filesArray.filter((file) => convertBytesToMB(file.size, 1) > 5);
      if (filesTooLarge.length > 0) setError('errors.files.tooLarge'); // show error for too large files
      const validatedFiles = filesArray.filter((file) => convertBytesToMB(file.size, 1) <= 5);
      const results = await Promise.allSettled(
        validatedFiles.map(async (file) => await convertFileToBase64(file))
      );
      const screenshots = results.map((result: any) => result.value);
      const { value: uploadedImages } = input || {};
      const allImages = [...uploadedImages, ...screenshots];
      const duplicates = findDuplicatesInArray(allImages);
      const filesDataUrls = filesArray.map((file: File) => {
        return window.URL.createObjectURL(file);
      });
      const imagesDimensions: any[] = [];
      for (const dataUrl of filesDataUrls) {
        const imageDimensions = await getImgDimensions(dataUrl);
        imagesDimensions.push(imageDimensions);
      }
      const invalidDimensions: any[] = [];
      imagesDimensions.forEach((imageDimensions) => {
        if (
          imageDimensions.height !== allowedImageDimensions.height ||
          imageDimensions.width !== allowedImageDimensions.width
        ) {
          setError('errors.files.invalidResolution');
          invalidDimensions.push(imageDimensions);
        }
      });
      if (duplicates.length > 0) {
        setError('errors.files.duplicate'); // show error for duplicate files
        duplicates.forEach((duplicate: any) => {
          const index = allImages.indexOf(duplicate);
          if (index > -1) {
            allImages.splice(index, 1);
          }
        });
      }
      if (allImages.length > maxFiles || invalidDimensions.length > 0) return;
      input.onChange(allImages);
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [input, maxFiles]
  );

  return (
    <FileUpload
      {...input}
      {...otherProps}
      maxFiles={maxFiles}
      onChange={handleChange}
      ref={inputRef}
      error={error}
    />
  );
};

export default ScreenshotsUploadWrapper;
